import useMedia from "utils/hooks/useMedia";
import React, { PropsWithChildren, useEffect, useMemo } from "react";
import { BrandingContext } from "@shared/features/common/Branding.context";
import { useExternalInviteToken } from "features/configuration/InviteExternalUsers/hooks.tsx";
import { sharedLib } from "@shared/services/shared.service.ts";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetUserQuery } from "@shared/utils/hooks/misc.ts";
import QueriesError from "common/Empty/QueriesError.tsx";
import { selectIsDarkMode } from "@shared/slices/common.slice.ts";
import AppTheme, { AppThemeType } from "@shared/common/Themes/AppTheme.ts";
import DarkModeTheme from "@shared/common/Themes/DarkModeTheme.ts";
import NewLookTheme from "@shared/common/Themes/NewLookTheme.ts";
import NewLookDarkTheme from "@shared/common/Themes/NewLookDarkTheme.ts";
import {
  AUTH_PATHS,
  OAUTH_AUTHORIZE_LOGIN_PATH,
  ONBOARDING_PATHS,
  TELEGRAM_AUTH_PATH,
} from "common/pathConstants";
import useRoutesMatch from "utils/hooks/useRoutesMatch";
import { useNewLook } from "features/newLook/NewLook.context.ts";
import useCanToggleNewLook from "features/newLook/useCanToggleNewLook.ts";
import useIsNewLookForced from "features/newLook/useIsNewLookForced.ts";
import { ignoreFailure } from "features/newLook/newLookStorage.ts";
import mixpanel from "mixpanel-browser";
import { ThemeProvider } from "styled-components";
import BottomNavigatorProvider from "features/misc/BottomNavigatorProvider.tsx";
import { NEW_LOOK_PHONE_MAX_WIDTH } from "common/NewLook/layout.ts";
import i18n from "i18next";
import {
  hasExplicitLanguageSelection,
  isSupportedLanguage,
} from "utils/language.ts";
import { shouldFetchWhitelabelConfiguration } from "features/misc/BrandingProvider.utils.ts";

const BrandingProvider = ({ children }: PropsWithChildren) => {
  const isDarkMode = useSelector(selectIsDarkMode);
  const isPhone = useMedia(
    [`(max-width: ${NEW_LOOK_PHONE_MAX_WIDTH})`],
    [true],
    false
  );
  const { isNewLook } = useNewLook();
  const canToggleNewLook = useCanToggleNewLook();
  const isNewLookForced = useIsNewLookForced();
  const isAuthRoute = !!useRoutesMatch([
    ...AUTH_PATHS,
    OAUTH_AUTHORIZE_LOGIN_PATH,
    TELEGRAM_AUTH_PATH,
  ]);
  const isOnboardingRoute = !!useRoutesMatch(ONBOARDING_PATHS);
  const externalInviteToken = useExternalInviteToken();
  const getRealUserQuery = useGetUserQuery(false);
  const user = useSelector(sharedLib.selectors.authentication.selectRealUser);

  const args = useMemo(() => {
    if (externalInviteToken) return { authToken: externalInviteToken };
    if (getRealUserQuery.isLoading || getRealUserQuery.isFetching)
      return skipToken;
    if (!shouldFetchWhitelabelConfiguration(user)) return skipToken;
    return undefined;
  }, [
    externalInviteToken,
    getRealUserQuery.isFetching,
    getRealUserQuery.isLoading,
    user,
  ]);

  const getWhitelabelConfigurationStatus =
    sharedLib.sharedApi.settings.useGetWhitelabelConfigurationQuery(args, {
      refetchOnMountOrArgChange: true,
    });
  const brandingData = getWhitelabelConfigurationStatus.currentData;
  const isWhitelabel = !!brandingData?.whitelabel;
  const allowViewOnlyUsersToViewBots =
    !!brandingData?.allow_view_only_users_to_view_bots;
  const isError = getWhitelabelConfigurationStatus.isError;
  // The platform follows the toggle only for users with the new_design flag,
  // and is always in the new design for users with the new_design_only flag;
  // login, register and onboarding are always in the new design.
  const isOptedIntoNewLook =
    isNewLookForced || (isNewLook && canToggleNewLook);
  const baseTheme = getBaseTheme(
    isDarkMode,
    isOptedIntoNewLook ||
      isAuthRoute ||
      isOnboardingRoute ||
      !!externalInviteToken,
    isPhone
  );
  const primaryColor = isDarkMode
    ? brandingData?.dark_mode_primary_color
    : brandingData?.light_mode_primary_color;
  const theme = useMemo(() => {
    const brandedTheme =
      isWhitelabel && primaryColor
        ? createThemeWithPrimaryColor(baseTheme, primaryColor)
        : baseTheme;
    return isOptedIntoNewLook
      ? { ...brandedTheme, hasNewLookBreakpoints: true }
      : brandedTheme;
  }, [baseTheme, isWhitelabel, primaryColor, isOptedIntoNewLook]);

  useEffect(() => {
    ignoreFailure(() => mixpanel.register({ new_look: isOptedIntoNewLook }));
  }, [isOptedIntoNewLook]);

  useEffect(() => {
    const language = brandingData?.language;
    if (!isSupportedLanguage(language) || hasExplicitLanguageSelection()) {
      return;
    }
    if (i18n.language !== language) {
      i18n.changeLanguage(language).catch(() => {});
    }
  }, [brandingData?.language]);

  const value = useMemo(
    () => ({
      isWhitelabel,
      allowViewOnlyUsersToViewBots,
      brandingData,
      getBrandingDataStatus: getWhitelabelConfigurationStatus,
    }),
    [
      isWhitelabel,
      allowViewOnlyUsersToViewBots,
      brandingData,
      getWhitelabelConfigurationStatus,
    ]
  );

  const content = isError ? (
    <QueriesError
      isGeneralPageError={true}
      description={i18n.t("common:errors.contact_support_if_persists")}
    />
  ) : (
    children
  );

  return (
    <ThemeProvider theme={theme}>
      <BottomNavigatorProvider>
        <BrandingContext.Provider value={value}>
          {content}
        </BrandingContext.Provider>
      </BottomNavigatorProvider>
    </ThemeProvider>
  );
};

export default BrandingProvider;

const webNewLookTypography = {
  ...NewLookTheme.typography,
  fontFamily:
    '"Inter Variable", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};
const webNewLookTheme = { ...NewLookTheme, typography: webNewLookTypography };
const webNewLookDarkTheme = {
  ...NewLookDarkTheme,
  typography: webNewLookTypography,
};

const mobileNewLookTheme = {
  ...webNewLookTheme,
  isPhone: true,
  // White page like desktop; cards are borderless with a slightly firmer
  // shadow.
  colors: { ...webNewLookTheme.colors, layoutBgColor: "#FFFFFF" },
  shadows: {
    ...webNewLookTheme.shadows,
    dashboardCard:
      "0 1px 2px rgba(16, 24, 40, 0.05), 0 4px 14px rgba(28, 43, 78, 0.07)",
    panel:
      "0 1px 2px rgba(16, 24, 40, 0.05), 0 4px 14px rgba(28, 43, 78, 0.07)",
    card: "0 3px 12px rgba(28, 43, 78, 0.06)",
  },
};
const mobileNewLookDarkTheme = {
  ...webNewLookDarkTheme,
  isPhone: true,
  shadows: {
    ...webNewLookDarkTheme.shadows,
    dashboardCard: "0 2px 8px rgba(0, 0, 0, 0.18)",
    panel: "none",
    card: "0 3px 12px rgba(0, 0, 0, 0.24)",
  },
};

const getBaseTheme = (
  isDarkMode: boolean,
  isNewLook: boolean,
  isPhone: boolean
): AppThemeType => {
  if (isNewLook) {
    if (isPhone)
      return isDarkMode ? mobileNewLookDarkTheme : mobileNewLookTheme;
    return isDarkMode ? webNewLookDarkTheme : webNewLookTheme;
  }
  return (isDarkMode ? DarkModeTheme : AppTheme) as AppThemeType;
};

const createThemeWithPrimaryColor = (
  theme: AppThemeType,
  primaryColor: string
): AppThemeType => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: primaryColor,
    primaryBlue: primaryColor,
    accentBlue: primaryColor,
    buttonBgColor: primaryColor,
    graphColor: primaryColor,
    blues: [...theme.colors.blues.slice(0, -1), primaryColor],
  },
});
